import { Link } from 'react-router';
import { useState, useEffect } from 'react';
import { Calendar, User } from 'lucide-react';
import { getAllNews } from '@/services/api';
import type { NewsItem } from '@/data/news';
import { useLanguage } from '@/contexts/LanguageContext';
import { ImageWithFallback } from '@/app/components/figma/ImageWithFallback';
import { generateNewsUrl } from '@/utils/slug';

export function NewsPage() {
  const { language, t } = useLanguage();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadNews() {
      setLoading(true);
      const data = await getAllNews();
      // Filter out null or invalid news items
      const validNews = data.filter(news => 
        news && 
        news.id && 
        news.title && 
        news.date && 
        news.image
      );
      setNewsItems(validNews);
      setLoading(false);
    }
    loadNews();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'hu' ? 'hu-HU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex justify-center items-center h-64">
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="grid md:grid-cols-2 gap-12">
          {newsItems.map((item) => (
            <article key={item.id} className="group">
              <Link to={`/news/${generateNewsUrl(item)}`}>
                <div className="relative h-80 mb-6 overflow-hidden">
                  <ImageWithFallback
                    src={item.image}
                    alt={item.title[language]}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar size={16} />
                      {formatDate(item.date)}
                    </span>
                  </div>
                  
                  <h2 className="text-2xl group-hover:text-gray-600 transition-colors">
                    {item.title[language]}
                  </h2>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {item.excerpt[language]}
                  </p>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}